import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Button } from 'components/Button';
import { Link } from 'components/Link';
import { Modal } from 'components/Modal';
import { Notification } from 'lib/notifications/notifications';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPrincipal } from 'utils/principal-service/principal-endpoints';
import {
  PrincipalInformationAndFeatures,
  PrincipalModalTypes,
} from 'utils/types';
import { EditDataAccess } from './EditDataAccess';
import { EditPrincipalInformation } from './EditPrincipalInformation';

export interface CreatePrincipalModalProps {
  onClose: () => void;
}

enum CreatePrincipalPage {
  PrincipalInformation,
  DataAccess,
}

// The division by 2 is needed because TypeScript numeric enums produce both forward and reverse mappings in the compiled keys.
const TOTAL_PAGES = Object.keys(CreatePrincipalPage).length / 2;

const createEmptyPrincipalInformation =
  (): PrincipalInformationAndFeatures => ({
    id: '',
    name: '',
    manufacturerName: '',
    slug: '',
    adGroupId: '',
    portfolioLevel: 2,
    teamId: '',
    logoUrl: '',
    disabledFeatures: [],
    maintenanceMode: [],
    customerData: {},
  });

/**
 * Modal for creating a new Principal. Presents two sequential subpages:
 * principal information (page 1) and data access configuration (page 2).
 */
export const CreatePrincipalModal: React.FC<CreatePrincipalModalProps> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState<CreatePrincipalPage>(
    CreatePrincipalPage.PrincipalInformation,
  );
  const [principalInformation, setPrincipalInformation] =
    useState<PrincipalInformationAndFeatures>(
      createEmptyPrincipalInformation(),
    );

  const handleBack = () => {
    /**
     * Clear any Data Access settings, as Principal PRI might change
     * and invalidate any selected options.
     */
    setPrincipalInformation((value) => ({
      ...value,
      disabledFeatures: [],
      maintenanceMode: [],
      customerData: {},
    }));
    setCurrentPage(CreatePrincipalPage.PrincipalInformation);
  };

  const handleConfirm = async () => {
    try {
      await createPrincipal(principalInformation);
      queryClient.invalidateQueries({ queryKey: ['principals'] });
      onClose();
    } catch (error) {
      console.error(error);
      const message =
        error instanceof AxiosError
          ? error.response?.data.message
          : error instanceof Error
          ? error.message
          : error;

      Notification({
        message: t(
          'features:admin-center:principalAdministration:createPrincipalModal:error',
          { message },
        ),
        type: 'error',
      });
    }
  };

  const isFirstPageValid =
    principalInformation.id.trim() !== '' &&
    principalInformation.name.trim() !== '' &&
    principalInformation.manufacturerName.trim() !== '' &&
    principalInformation.slug.trim() !== '' &&
    principalInformation.adGroupId?.trim() !== '';

  return (
    <Modal
      isBig
      headingAlign="left"
      heading={t(
        'features:admin-center:principalAdministration:createPrincipalModal:title',
      )}
      hideAllButtons
      onCancel={onClose}
    >
      <div className="w-full">
        <div className="max-w-xl-content mx-auto">
          <h3 className="text-grey-700 mb-4 mt-5 text-lg font-semibold">
            {currentPage === CreatePrincipalPage.PrincipalInformation
              ? t(
                  'features:admin-center:principalAdministration:createPrincipalModal:page1:subtitle',
                )
              : t(
                  'features:admin-center:principalAdministration:createPrincipalModal:page2:subtitle',
                )}
            {` (${currentPage + 1}/${TOTAL_PAGES})`}
          </h3>
          {currentPage === CreatePrincipalPage.PrincipalInformation ? (
            <EditPrincipalInformation
              principalInformation={principalInformation}
              principalModalType={PrincipalModalTypes.CREATE}
              onChange={setPrincipalInformation}
            />
          ) : (
            <EditDataAccess
              principalInformation={principalInformation}
              onChange={setPrincipalInformation}
            />
          )}
          <hr className="h-1px bg-grey-300 mx-auto w-full rounded border-0" />
        </div>
      </div>
      <div className="max-w-content mt-6 flex w-full justify-between">
        {currentPage === CreatePrincipalPage.PrincipalInformation ? (
          <>
            <div className="mt-2">
              <Link label={t('labels:cancel')} onClick={onClose} />
            </div>
            <Button
              label={t(
                'features:admin-center:principalAdministration:createPrincipalModal:continueToDataAccess',
              )}
              onClick={() => {
                setCurrentPage(CreatePrincipalPage.DataAccess);
              }}
              disabled={!isFirstPageValid}
            />
          </>
        ) : (
          <>
            <div className="mt-2">
              <Link
                label={t(
                  'features:admin-center:principalAdministration:createPrincipalModal:back',
                )}
                onClick={handleBack}
              />
            </div>
            <Button
              label={t(
                'features:admin-center:principalAdministration:createPrincipalModal:createPrincipal',
              )}
              onClick={handleConfirm}
            />
          </>
        )}
      </div>
    </Modal>
  );
};
